// web/src/stores/agentsStore.ts
// Manages the agent roster: system agents (from GET /api/agents) + custom user agents.
// No localStorage persistence — agent roster is fetched from the API on every session.
import { create } from "zustand";
import { useAuthStore } from "./authStore";
import { api } from "../lib/apiClient";
import { DEFAULT_AGENT_ID } from "../lib/agentConfig";
export const useAgentsStore = create()((set, get) => ({
    systemAgents: [],
    customAgents: [],
    selectedAgentId: DEFAULT_AGENT_ID,
    agentMode: "orchestrated",
    isLoadingSystem: false,
    loadError: null,
    loadSystemAgents: async () => {
        if (!useAuthStore.getState().accessToken)
            return;
        set({ isLoadingSystem: true, loadError: null });
        try {
            const data = await api.getAgents();
            set({
                systemAgents: data.agents.filter((a) => !a.roster_excluded),
                isLoadingSystem: false,
            });
        }
        catch (e) {
            set({ loadError: String(e), isLoadingSystem: false });
        }
    },
    loadCustomAgents: async () => {
        if (!useAuthStore.getState().accessToken)
            return;
        try {
            const data = await api.listUserAgents();
            const custom = data.agents.map((a) => ({
                id: a.agent_id,
                name: a.agent_name,
                persona: a.persona,
                tools: a.tools,
                skills: a.skills,
                memory: {
                    short_term: a.memory.short_term ?? true,
                    long_term: a.memory.long_term ?? false,
                },
                tone: a.tone,
                created_at: a.created_at,
                updated_at: a.updated_at ?? a.created_at,
            }));
            set({ customAgents: custom });
        }
        catch {
            // Non-fatal: custom agents fail gracefully
        }
    },
    selectAgent: (agentId) => {
        let mode;
        if (agentId === DEFAULT_AGENT_ID) {
            mode = "orchestrated";
        }
        else if (agentId.startsWith("user/")) {
            mode = "custom";
        }
        else {
            mode = "direct";
        }
        set({ selectedAgentId: agentId, agentMode: mode });
    },
    addCustomAgent: (agent) => {
        set((s) => ({ customAgents: [...s.customAgents, agent] }));
    },
    updateCustomAgent: (id, patch) => {
        set((s) => ({
            customAgents: s.customAgents.map((a) => a.id === id ? { ...a, ...patch, updated_at: new Date().toISOString() } : a),
        }));
    },
    deleteCustomAgent: (id) => {
        const { selectedAgentId } = get();
        // Optimistic UI update — remove from local state immediately
        set((s) => ({
            customAgents: s.customAgents.filter((a) => a.id !== id),
            selectedAgentId: selectedAgentId === id ? DEFAULT_AGENT_ID : s.selectedAgentId,
            agentMode: selectedAgentId === id ? "orchestrated" : s.agentMode,
        }));
        // Extract agent_name from id "user/{uid}/{name}" and call API
        const parts = id.split("/");
        const agentName = parts[parts.length - 1];
        if (agentName)
            void api.deleteUserAgent(agentName).catch(() => undefined);
    },
}));
/** All agents visible in the roster (system + custom), in display order */
export function useRosterAgents() {
    const systemAgents = useAgentsStore((s) => s.systemAgents);
    const customAgents = useAgentsStore((s) => s.customAgents);
    // Default agent first, then other system agents, then custom
    const luna = systemAgents.find((a) => a.agent_id === DEFAULT_AGENT_ID);
    const others = systemAgents.filter((a) => a.agent_id !== DEFAULT_AGENT_ID);
    return { luna, systemAgents: others, customAgents };
}
